import './index.css'
import { plural } from '@disco/utils/string-utils'
import { Theme } from '@radix-ui/themes'
import cn from 'clsx'

import React, { useContext, useEffect } from 'react'
import { useSelector } from 'hooks/use-redux'
import { createPortal } from 'react-dom'
import { getTrackPlaying } from '@disco/state-management/selectors/media'
import { IndicatorContext } from 'contexts/indicator'
import { Flex } from 'components/ds'
import { ArrowUpThick } from 'components/ui/icon'
import Text from 'components/ui/text'
import CircleProgress from './circle-progress'
type Props = {
  title: string
  left: React.ReactNode
  mainIndicator?: boolean
  portal?: boolean
  onClick?: () => void
}
const appNode =
  document.getElementById('app') || document.getElementById('__next')

/**
 * A drop indicator component that displays feedback during drag and drop operations
 * @param title - The text to display in the indicator
 * @param left - React node to display on the left side of the indicator
 * @param mainIndicator - Whether this is the main indicator that prevents other indicators from showing - defaults to false
 * @param portal - Whether to render the indicator in a portal - defaults to false
 * @param onClick - Optional callback function called when the indicator is clicked
 * @returns A themed indicator component or null if another main indicator is displayed
 */
const DropIndicator = ({
  title,
  left,
  mainIndicator,
  portal,
  onClick,
}: Props): null | React.ReactNode => {
  const playerVisible = useSelector(state => !!getTrackPlaying(state))
  const isIndicatorVisible =
    playerVisible !== null ? playerVisible.toString() : null
  const { mainIndicatorDisplayed, setMainIndicatorDisplayed } =
    useContext(IndicatorContext)
  useEffect(() => {
    if (mainIndicator) setMainIndicatorDisplayed(true)
    return () => {
      if (mainIndicator) setMainIndicatorDisplayed(false)
    }
  }, [mainIndicator, setMainIndicatorDisplayed])
  if (!mainIndicator && mainIndicatorDisplayed) return null
  const indicator = (
    <Theme appearance="dark" hasBackground={false}>
      <div
        className={cn('DropIndicator', {
          'DropIndicator--clickable': !!onClick,
        })}
        onClick={onClick}
        data-player-visible={isIndicatorVisible}
      >
        <Flex align="center" gap="2">
          {left}
          <Text type="paragraph">{title}</Text>
        </Flex>
      </div>
    </Theme>
  )
  return portal && appNode ? createPortal(indicator, appNode) : indicator
}

export const DropFilesIndicator = (): React.ReactNode => (
  <DropIndicator
    title="Drop files to upload to DISCO"
    left={<CircleProgress done icon={ArrowUpThick} />}
    mainIndicator
    portal
  />
)
export const UploadIndicator = ({
  tracksNumber,
  progress,
  done = false,
  portal = false,
  onClick,
}: {
  tracksNumber: number
  progress?: number
  done?: boolean
  portal?: boolean
  onClick?: () => void
}): null | React.ReactNode => {
  if (tracksNumber < 1) return null
  const things = plural(tracksNumber, 'track', 'tracks')
  const title = done ? `${things} uploaded` : `Uploading ${things}`
  const indeterminate = progress === undefined || progress === 0
  return (
    <DropIndicator
      title={title}
      left={
        <CircleProgress
          progress={(progress || 25) / 100}
          done={done}
          indeterminate={indeterminate}
        />
      }
      onClick={onClick}
      portal={portal}
    />
  )
}
